import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, GiftIcon } from 'lucide-react';
const BONUS_COUNT = 6;
export function BonusGrid() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const scrollToIndex = useCallback((index: number) => {
    const carousel = scrollRef.current;
    if (!carousel) return;
    carousel.scrollTo({
      left: index * carousel.offsetWidth,
      behavior: 'smooth'
    });
    setActiveIndex(index);
  }, []);
  const handleScroll = () => {
    const carousel = scrollRef.current;
    if (!carousel) return;
    const newIndex = Math.round(carousel.scrollLeft / carousel.offsetWidth);
    setActiveIndex(current => current === newIndex ? current : newIndex);
  };
  useEffect(() => {
    const section = sectionRef.current;
    if (!section || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => setIsAutoPlaying(entry.isIntersecting), {
      threshold: 0.35
    });
    observer.observe(section);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = window.setInterval(() => {
      setActiveIndex(current => {
        const next = (current + 1) % BONUS_COUNT;
        scrollToIndex(next);
        return next;
      });
    }, 6500);
    return () => window.clearInterval(interval);
  }, [isAutoPlaying, scrollToIndex]);
  const bonuses = [{
    id: 1,
    badge: 'BONUS 1',
    title: 'Conexiones PRO Sin Fallas',
    description: 'Aprende a conectar tiras LED, empalmes y uniones correctas sin errores para garantizar la durabilidad de tus trabajos.',
    image: 'https://cdn.magicpatterns.com/uploads/84u9VCBsLpijCXuzGwbgBJ/no_modificar_nada_de_nada_202605232240.webp',
    originalPrice: '15.000'
  }, {
    id: 2,
    badge: 'BONUS 2',
    title: 'Cartelería LED Profesional',
    description: 'Aprende desde lo básico hasta crear cartelería LED profesional paso a paso, dominando las técnicas más avanzadas del mercado.',
    image: 'https://cdn.magicpatterns.com/uploads/ksD1FmSWKVD25f5QbKbt7d/Bonus_2_compri.webp',
    originalPrice: '12.500'
  }, {
    id: 3,
    badge: 'BONUS 3',
    title: 'Sistema 3D de Cero a Profesional',
    description: 'Aprende desde lo básico hasta dominar la creación de letras y carteles 3D profesionales paso a paso con acabados de lujo.',
    image: 'https://cdn.magicpatterns.com/uploads/h8PPXg83CwwdLFkihSAAzn/Bonus_3_compri.webp',
    originalPrice: '18.000'
  }, {
    id: 4,
    badge: 'BONUS 4',
    title: 'Tips y Recomendaciones',
    description: 'Consejos prácticos, trucos y recomendaciones clave para evitar errores comunes y lograr acabados profesionales en cada proyecto.',
    image: 'https://cdn.magicpatterns.com/uploads/mKf3z6vufeBqCfubdA69PW/bonus_4_compri.webp',
    originalPrice: '14.500'
  }, {
    id: 5,
    badge: 'BONUS 5',
    title: 'Sistema de Marketing Visual',
    description: 'Aprende a comunicar, atraer y convertir más clientes con estrategias de marketing visual efectivas para negocios que destacan.',
    image: 'https://cdn.magicpatterns.com/uploads/3D9mc8QXWVkJVezyvNfmuq/Bonus_5_compri.webp',
    originalPrice: '11.000'
  }, {
    id: 6,
    badge: 'BONUS 6',
    title: 'Proveedores y Materiales',
    description: 'Todo lo que necesitas para tus proyectos. Acceso a nuestra lista exclusiva de proveedores confiables y materiales de alta calidad.',
    image: 'https://cdn.magicpatterns.com/uploads/eiHvcsMKVwMmUKKte9GGWF/Bonus_6_compri.webp',
    originalPrice: '20.000'
  }];
  return <section ref={sectionRef} className="py-20 px-4 relative z-10 bg-transparent overflow-hidden border-y border-white/5 pt-[30px] pb-[30px]" data-id="element-53" data-mp="11x8uernofp2m">
      <div className="max-w-7xl mx-auto" data-id="element-54" data-mp="16vtn860ln6h1">
        <div className="text-center mb-16" data-id="element-55" data-mp="swu8pr6sw7yl">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#ec4899]/10 border border-[#ec4899]/30 text-[#ec4899] text-sm font-black mb-6 tracking-widest" data-id="element-56" data-mp="svsl6n8llszz">
            <GiftIcon className="w-4 h-4" data-id="element-57" data-mp="e7o2hj9jphh7" />
            OFERTA EXCLUSIVA
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-6xl font-black text-white mb-6 tracking-tight leading-tight" data-id="element-58" data-mp="7wp9kqus549">
            TODO LO QUE <span className="text-[#00f0ff]" data-id="element-59" data-mp="3ctu0qaxb0rem">TE LLEVÁS HOY</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-xl text-gray-400 max-w-2xl mx-auto font-medium" data-id="element-60" data-mp="3qsl8zgphjiuu">
            El curso completo más 6 bonus de regalo por un precio único
          </motion.p>
        </div>

        <div className="relative group/carousel" onMouseEnter={() => setIsAutoPlaying(false)} onMouseLeave={() => setIsAutoPlaying(true)} data-id="element-61" data-mp="9d4uyr5jplq0">
          <div className="absolute top-1/2 -left-4 md:-left-12 -translate-y-1/2 z-20" data-id="element-62" data-mp="id9wwlryzrws">
            <button onClick={() => scrollToIndex(Math.max(0, activeIndex - 1))} disabled={activeIndex === 0} className={`p-4 rounded-full bg-[#1a0b2e] border border-white/10 text-white transition-all ${activeIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:border-[#ec4899] hover:text-[#ec4899] shadow-[0_0_15px_rgba(236,72,153,0.3)]'}`} data-id="element-63" data-mp="3c4zu59tdfp5i">
              <ChevronLeft className="w-6 h-6" data-id="element-64" data-mp="3utoenunxzsgr" />
            </button>
          </div>

          <div className="absolute top-1/2 -right-4 md:-right-12 -translate-y-1/2 z-20" data-id="element-65" data-mp="3mbtl4jxuuvk">
            <button onClick={() => scrollToIndex(Math.min(bonuses.length - 1, activeIndex + 1))} disabled={activeIndex === bonuses.length - 1} className={`p-4 rounded-full bg-[#1a0b2e] border border-white/10 text-white transition-all ${activeIndex === bonuses.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:border-[#ec4899] hover:text-[#ec4899] shadow-[0_0_15px_rgba(236,72,153,0.3)]'}`} data-id="element-66" data-mp="8nrix8t5fnjr">
              <ChevronRight className="w-6 h-6" data-id="element-67" data-mp="2256xhyuwsqte" />
            </button>
          </div>

          <div ref={scrollRef} onScroll={handleScroll} className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar gap-8 pb-10" style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }} data-id="element-68" data-mp="1x62mr3sc6l0y">
            {bonuses.map((bonus, index) => <div key={bonus.id} className="min-w-full md:min-w-[calc(50%-16px)] lg:min-w-[calc(33.333%-22px)] snap-center" data-id="element-69" data-mp="2kwg9ces5jro5" data-mpi={index}>
                <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: index * 0.1
            }} className="h-full bg-[#1a0b2e]/60 backdrop-blur-md border border-white/10 rounded-[2.5rem] p-8 hover:border-[#ec4899]/50 transition-all duration-500 hover:shadow-[0_20px_60px_rgba(236,72,153,0.15)] group flex flex-col" data-id="element-70" data-mp="2bw1o1ff6uckn">
                  <div className="inline-flex self-start items-center gap-2 px-4 py-1.5 rounded-full bg-[#ec4899]/10 border border-[#ec4899]/30 text-[#ec4899] text-xs font-black mb-6 tracking-widest" data-id="element-71" data-mp="3ja5881w7pzww">
                    {bonus.badge}
                  </div>

                  <h3 className="text-white font-black text-2xl mb-4 leading-tight group-hover:text-[#00f0ff] transition-colors" data-id="element-72" data-mp="3ehubf7wrqu8k">
                    {bonus.title}
                  </h3>

                  <p className="text-gray-400 text-base mb-8 leading-relaxed flex-grow font-medium" data-id="element-73" data-mp="at0r1w3qfwsy">
                    {bonus.description}
                  </p>

                  <div className="relative mb-8 overflow-hidden rounded-2xl group-hover:scale-105 transition-transform duration-500 shadow-2xl" data-id="element-74" data-mp="1vhla4k5tuiq0">
                    <img src={bonus.image} alt={bonus.title} width={400} height={400} loading="lazy" decoding="async" className="w-full h-auto object-cover" data-id="element-75" data-mp="24bhtxsbirj29" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a0b2e] via-transparent to-transparent opacity-40" data-id="element-76" data-mp="17msc21zw6qga" />
                  </div>

                  <div className="text-center mt-auto" data-id="element-77" data-mp="1gm5c6424m2oc">
                    <p className="text-gray-500 text-xs font-bold mb-2 tracking-widest uppercase" data-id="element-78" data-mp="1gklvwdggzrmk">
                      VALOR REAL:
                    </p>
                    <p className="text-2xl text-gray-600 line-through font-black decoration-[#ec4899]/60 decoration-4 mb-4" data-id="element-79" data-mp="2s1txys3n8vnk">
                      {bonus.originalPrice} ARS
                    </p>
                    <div className="bg-gradient-to-r from-[#ec4899] to-[#8b5cf6] text-white font-black text-xl py-4 px-6 rounded-2xl uppercase tracking-widest shadow-[0_5px_20px_rgba(236,72,153,0.3)]" data-id="element-80" data-mp="23b8452uwjl8w">
                      INCLUIDO HOY
                    </div>
                  </div>
                </motion.div>
              </div>)}
          </div>

          <div className="flex justify-center gap-3 mt-4" data-id="element-81" data-mp="1pi8rd8mi8551">
            {bonuses.map((_, index) => <button key={index} onClick={() => scrollToIndex(index)} className={`h-2.5 rounded-full transition-all duration-300 ${activeIndex === index ? 'w-10 bg-[#ec4899] shadow-[0_0_10px_rgba(236,72,153,0.5)]' : 'w-2.5 bg-white/10 hover:bg-white/20'}`} data-id="element-82" data-mp="1knbgdw5nzlpi" data-mpi={index} />)}
          </div>
        </div>
      </div>
    </section>;
}